/**
 * LocalStorage utility functions with type safety
 */

import { STORAGE_KEYS } from "./constants";
import { logger } from "@/utils/logger"
/**
 * Get session token from localStorage
 */
export const getSessionToken = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN);
  } catch (error) {
    logger.error("Error getting session token:", error);
    return null;
  }
};

/**
 * Set session token in localStorage
 */
export const setSessionToken = (token: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token);
  } catch (error) {
    logger.error("Error setting session token:", error);
  }
};

/**
 * Clear session token from localStorage
 */
export const clearSessionToken = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
  } catch (error) {
    logger.error("Error clearing session token:", error);
  }
};

/**
 * Get item from localStorage with JSON parsing
 */
export const getStorageItem = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    logger.error(`Error getting storage item ${key}:`, error);
    return null;
  }
};

/**
 * Set item in localStorage with JSON stringification
 */
export const setStorageItem = <T>(key: string, value: T): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    logger.error(`Error setting storage item ${key}:`, error);
  }
};

/**
 * Remove item from localStorage
 */
export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logger.error(`Error removing storage item ${key}:`, error);
  }
};

/**
 * Clear all items from localStorage
 */
export const clearAllStorage = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    logger.error("Error clearing storage:", error);
  }
};

/**
 * Store the intended redirect URL after login
 * Used when user tries to access protected route while not authenticated
 */
export const setRedirectUrl = (url: string): void => {
  try {
    localStorage.setItem("redirect_after_login", url);
  } catch (error) {
    logger.error("Error setting redirect URL:", error);
  }
};

/**
 * Get and clear the stored redirect URL
 */
export const getAndClearRedirectUrl = (): string | null => {
  try {
    const url = localStorage.getItem("redirect_after_login");
    if (url) {
      localStorage.removeItem("redirect_after_login");
    }
    return url;
  } catch (error) {
    logger.error("Error getting redirect URL:", error);
    return null;
  }
};
